import { useState, useRef } from "react";
import { base44 } from "@/api/base44Client";
import { useApp } from "@/lib/AppContext";


export default function EmailOtpVerify({ phone, onVerified, onBack }) {
  const { lang } = useApp();
  const isAr = lang === "ar";

  const [step, setStep] = useState("email"); // email | otp
  const [email, setEmail] = useState("");
  const otpRef = useRef("");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const inputRef = useRef(null);

  const generateOtp = () => Math.floor(100000 + Math.random() * 900000).toString();

  const startTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(prev => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const sendOtpEmail = async (emailAddr, code) => {
    const response = await base44.functions.invoke("sendOtpEmail", { email: emailAddr, code, lang });
    if (response.data?.error) {
      throw new Error(response.data.error);
    }
  };

  const handleEmailSubmit = async () => {
    setError("");
    if (!email.includes("@") || !email.includes(".")) {
      setError(isAr ? "يرجى إدخال بريد إلكتروني صحيح" : "Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      // التحقق من أن البريد غير مسجل مسبقاً برقم آخر
      const existing = await base44.entities.Customer.filter({ email });
      if (existing.length > 0) {
        setError(isAr
          ? "⚠️ هذا البريد الإلكتروني مسجل مسبقاً برقم جوال آخر. يرجى استخدام بريد إلكتروني مختلف"
          : "⚠️ This email is already registered with another phone number. Please use a different email");
        setLoading(false);
        return;
      }
      const code = generateOtp();
      otpRef.current = code;
      await sendOtpEmail(email, code);
      startTimer();
      setStep("otp");
      setTimeout(() => inputRef.current?.focus(), 100);
    } catch (err) {
      setError(isAr ? "فشل إرسال الكود، تحقق من البريد وحاول مرة أخرى" : "Failed to send code, check your email and try again");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (input.trim() === otpRef.current) {
      setLoading(true);
      const result = await onVerified(email);
      setLoading(false);
      if (result?.error) {
        setError(result.message || (isAr ? "حدث خطأ، حاول مرة أخرى" : "An error occurred, please try again"));
        setInput("");
      }
    } else {
      setError(isAr ? "الكود غير صحيح، حاول مرة أخرى" : "Incorrect code, please try again");
      setInput("");
    }
  };

  const handleResend = async () => {
    setError("");
    setInput("");
    setLoading(true);
    try {
      const code = generateOtp();
      otpRef.current = code;
      await sendOtpEmail(email, code);
      startTimer();
    } catch (err) {
      setError(isAr ? "فشل إعادة الإرسال، حاول مرة أخرى" : "Resend failed, please try again");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col items-center gap-6" dir={isAr ? "rtl" : "ltr"}>
      {/* Icon */}
      <div className="w-20 h-20 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center text-4xl shadow-md">
        {step === "email" ? "📧" : "🔐"}
      </div>

      {step === "email" ? (
        <>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-black text-foreground">
              {isAr ? "تحقق من هويتك" : "Verify Your Identity"}
            </h2>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto leading-relaxed">
              {isAr
                ? "رقمك غير مسجل في نظامنا. أدخل بريدك الإلكتروني وسنرسل لك كود التحقق"
                : "Your number isn't registered. Enter your email and we'll send you a verification code"}
            </p>
            <p className="font-bold text-primary text-base" dir="ltr">{phone}</p>
          </div>

          <div className="w-full max-w-sm space-y-3">
            <input
              type="email"
              value={email}
              onChange={e => { setEmail(e.target.value); setError(""); }}
              placeholder="example@email.com"
              className="w-full px-4 py-4 text-center border-2 border-border rounded-2xl focus:outline-none focus:border-primary transition-colors bg-secondary/30"
              dir="ltr"
              onKeyDown={e => e.key === "Enter" && handleEmailSubmit()}
            />
            {error && <p className="text-sm text-destructive text-center font-medium">{error}</p>}
            <button
              onClick={handleEmailSubmit}
              disabled={loading || !email}
              className="w-full py-3.5 bg-gradient-to-r from-primary to-accent text-white font-black rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? (isAr ? "جاري الإرسال..." : "Sending...")
                : (isAr ? "إرسال كود التحقق 📨" : "Send Verification Code 📨")}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-black text-foreground">
              {isAr ? "أدخل كود التحقق" : "Enter Verification Code"}
            </h2>
            <p className="text-muted-foreground text-sm max-w-xs mx-auto leading-relaxed">
              {isAr ? `تم إرسال الكود إلى:` : `Code sent to:`}
            </p>
            <p className="font-bold text-primary text-sm" dir="ltr">{email}</p>
            <div className="flex items-start gap-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 rounded-xl px-3 py-2 text-right max-w-xs mx-auto">
              <span className="text-amber-500 text-base mt-0.5">⚠️</span>
              <p className="text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
                {isAr
                  ? "إذا لم يصلك الكود، يرجى التحقق من مجلد البريد غير الهام (Spam) أو مجلد الترويج"
                  : "If you don't receive the code, please check your Spam or Promotions folder"}
              </p>
            </div>
          </div>

          <div className="w-full max-w-sm space-y-3">
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              value={input}
              onChange={e => { setInput(e.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
              placeholder="• • • • • •"
              maxLength={6}
              className="w-full px-4 py-4 text-center text-2xl font-black tracking-[0.5em] border-2 border-border rounded-2xl focus:outline-none focus:border-primary transition-colors bg-secondary/30"
              dir="ltr"
              onKeyDown={e => e.key === "Enter" && input.length === 6 && handleVerify()}
            />
            {error && <p className="text-sm text-destructive text-center font-medium">{error}</p>}
            <button
              onClick={handleVerify}
              disabled={input.length < 6}
              className="w-full py-3.5 bg-gradient-to-r from-primary to-accent text-white font-black rounded-xl shadow-md hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              ✅ {isAr ? "تحقق وأكمل" : "Verify & Continue"}
            </button>
          </div>

          <div className="text-center">
            {resendTimer > 0 ? (
              <p className="text-xs text-muted-foreground">
                {isAr ? `إعادة الإرسال بعد ${resendTimer} ثانية` : `Resend in ${resendTimer}s`}
              </p>
            ) : (
              <button onClick={handleResend} disabled={loading} className="text-sm text-primary font-bold underline">
                {loading ? "..." : (isAr ? "إعادة إرسال الكود" : "Resend code")}
              </button>
            )}
          </div>

          <button
            onClick={() => { setStep("email"); setInput(""); setError(""); }}
            className="text-xs text-muted-foreground hover:text-foreground underline transition-colors"
          >
            {isAr ? "← تغيير البريد الإلكتروني" : "← Change email"}
          </button>
        </>
      )}

      <button onClick={onBack} className="text-xs text-muted-foreground hover:text-foreground underline transition-colors">
        {isAr ? "← تغيير رقم الجوال" : "← Change phone number"}
      </button>
    </div>
  );
}